import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

function Row({ k, v, mono }) {
  return (
    <div className="py-2 flex items-baseline justify-between border-b border-line last:border-0">
      <span className="text-sub text-xs uppercase tracking-wider">{k}</span>
      <span className={`text-ink text-right ${mono ? "font-mono text-xs" : "text-sm"}`}>{v || "—"}</span>
    </div>
  );
}

export default function AdminComplianceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [ofacResult, setOfacResult] = useState("clear");
  const [ofacNotes, setOfacNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [screening, setScreening] = useState(false);
  const [screenErr, setScreenErr] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/users/${id}/compliance`).then((r) => setU(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!u) return <DashboardLayout><div className="p-12 overline">Loading…</div></DashboardLayout>;
  const c = u.compliance || {};
  const ofacUrl = "https://sanctionssearch.ofac.treas.gov/";
  const screen = c.sanctions_screening;

  const runScreening = async () => {
    setScreening(true);
    setScreenErr("");
    try {
      const r = await api.post(`/admin/users/${id}/compliance/screen`);
      if (r.data?.checked === false) setScreenErr(r.data?.error || "Screening did not run");
      // Auto-pre-fill the manual result picker based on screening output
      if (r.data?.has_sanction_match) setOfacResult("blocked");
      else if ((r.data?.top_score || 0) >= 0.7) setOfacResult("flagged");
      else setOfacResult("clear");
      load();
    } catch (e) {
      setScreenErr(e.response?.data?.detail || "Screening failed");
    } finally { setScreening(false); }
  };

  const decide = async (kyc_status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${id}/compliance`, {
        kyc_status, ofac_result: ofacResult, ofac_notes: ofacNotes,
      });
      load();
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout testid="admin-compliance-detail">
      <div className="p-8 md:p-12 max-w-5xl">
        <Link to="/admin/compliance" className="link text-sub text-xs">← Back to queue</Link>
        <div className="overline mt-4">{u.user_id}</div>
        <h1 className="h-serif text-5xl text-ink mt-2">{u.name}</h1>
        <div className="text-sub mt-1">{u.email} · {u.company || "—"}</div>

        <section className="mt-10" data-testid="profile-section">
          <div className="border border-line bg-parchment p-6" data-testid="profile-block">
            <div className="overline">Corporate profile</div>
            <h3 className="h-serif text-2xl text-ink mt-1 mb-4">{c.legal_entity_name || "Not submitted"}</h3>
            <Row k="Jurisdiction" v={c.jurisdiction} />
            <Row k="Registration #" v={c.registration_number} mono />
            <Row k="Beneficial owners" v={(c.beneficial_owners || []).join(", ")} />
            <Row k="Countries of operation" v={(c.countries_of_operation || []).join(", ")} />
            <Row k="PEP" v={c.is_pep ? `Yes — ${c.pep_details || "no details"}` : "No"} />
            <Row k="Attestations" v={
              c.attest_no_sanctioned_exposure && c.attest_source_of_funds && c.attest_beneficial_ownership_accuracy
                ? "All three signed ✓" : "Incomplete"
            } />
            <Row k="Submitted" v={c.submitted_at?.slice(0, 19).replace("T", " ")} mono />
          </div>
        </section>

        <section className="mt-10" data-testid="automated-screening-block">
          <div className="border border-line bg-parchment p-6">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <div className="overline">Automated sanctions screening</div>
                <h3 className="h-serif text-2xl text-ink mt-1">OpenSanctions · OFAC SDN · EU · UN · UK · Global PEPs</h3>
                <p className="text-sub text-sm mt-2 leading-relaxed max-w-2xl">
                  Runs the legal entity and every beneficial owner against the consolidated
                  OpenSanctions dataset. Results below auto-pre-fill the manual decision picker.
                </p>
              </div>
              <button onClick={runScreening} disabled={screening || !c.legal_entity_name}
                      className="btn-primary text-sm whitespace-nowrap"
                      data-testid="run-screening">
                {screening ? "Screening…" : (screen ? "Re-run screening" : "Run screening")}
              </button>
            </div>

            {screenErr && (
              <div className="mt-4 border-l-2 border-danger pl-3 text-xs text-danger" data-testid="screening-error">
                {screenErr}
              </div>
            )}

            {screen && (
              <div className="mt-6 border-t border-line pt-5" data-testid="screening-result">
                <div className="flex items-baseline justify-between text-xs font-mono text-sub">
                  <span>
                    Last run: {screen.checked_at?.slice(0, 19).replace("T", " ")} · {screen.checked_by}
                  </span>
                  <span>
                    Top score: <span className="text-ink">{((screen.top_score || 0) * 100).toFixed(1)}%</span>
                    {" · "}Matches: <span className="text-ink">{(screen.matches || []).length}</span>
                  </span>
                </div>

                {screen.has_sanction_match ? (
                  <div className="mt-3 px-3 py-2 border border-danger bg-danger/5 text-danger text-sm" data-testid="screening-verdict">
                    SANCTION MATCH detected — review every result below before clearing.
                  </div>
                ) : (screen.matches || []).length === 0 ? (
                  <div className="mt-3 px-3 py-2 border border-success bg-success/5 text-success text-sm" data-testid="screening-verdict">
                    Clear — no matches on consolidated sanctions or PEP lists.
                  </div>
                ) : (
                  <div className="mt-3 px-3 py-2 border border-warn bg-warn/5 text-warn text-sm" data-testid="screening-verdict">
                    Potential matches found — review each row below.
                  </div>
                )}

                {(screen.matches || []).length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-sub uppercase tracking-wider">
                        <tr className="border-b border-line">
                          <th className="text-left py-2 pr-3">Target</th>
                          <th className="text-left py-2 pr-3">Searched</th>
                          <th className="text-left py-2 pr-3">Match</th>
                          <th className="text-left py-2 pr-3">Score</th>
                          <th className="text-left py-2">Topics</th>
                        </tr>
                      </thead>
                      <tbody>
                        {screen.matches.map((m, i) => (
                          <tr key={i} className="border-b border-line/50 last:border-0">
                            <td className="py-2 pr-3 text-sub">{m.target_type}</td>
                            <td className="py-2 pr-3 text-ink">{m.searched_name}</td>
                            <td className="py-2 pr-3 text-ink">{m.caption}</td>
                            <td className={`py-2 pr-3 font-mono ${m.score >= 0.85 ? "text-danger" : m.score >= 0.7 ? "text-warn" : "text-sub"}`}>
                              {(m.score * 100).toFixed(1)}%
                            </td>
                            <td className="py-2">
                              <div className="flex flex-wrap gap-1">
                                {(m.topics || []).map((t) => (
                                  <span key={t} className="px-1.5 py-0.5 border border-line text-[10px] font-mono text-sub">{t}</span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="mt-8 grid md:grid-cols-1 gap-6">
          <div className="border border-line bg-parchment p-6" data-testid="ofac-block">
            <div className="overline">Manual review & decision input</div>
            <h3 className="h-serif text-2xl text-ink mt-1">Adjudicate result</h3>
            <p className="text-sub text-sm mt-3 leading-relaxed">
              The automated screening above pre-fills the result below. You can also cross-check
              against the U.S. Treasury OFAC SDN search directly.
            </p>
            <a href={ofacUrl} target="_blank" rel="noopener noreferrer"
               className="link text-sub text-xs mt-3 inline-block" data-testid="open-ofac">
              Open OFAC SDN search ↗
            </a>

            <div className="mt-6 space-y-3">
              <label className="block">
                <span className="overline block mb-2">Result</span>
                <select className="input-line" value={ofacResult} onChange={(e) => setOfacResult(e.target.value)} data-testid="ofac-result">
                  <option value="clear">Clear — no matches</option>
                  <option value="flagged">Flagged — needs further review</option>
                  <option value="blocked">Blocked — confirmed match</option>
                </select>
              </label>
              <label className="block">
                <span className="overline block mb-2">Notes</span>
                <textarea rows={2} className="input-line resize-none" value={ofacNotes} onChange={(e) => setOfacNotes(e.target.value)} data-testid="ofac-notes" />
              </label>
            </div>

            {c.ofac_check && (
              <div className="mt-5 border-t border-line pt-4 text-xs">
                <div className="overline">Previous adjudication</div>
                <div className="mt-1 font-mono text-sub">
                  {c.ofac_check.result} · {c.ofac_check.checked_by} · {c.ofac_check.checked_at?.slice(0, 19).replace("T", " ")}
                </div>
                {c.ofac_check.notes && <p className="mt-1 text-sub italic">{c.ofac_check.notes}</p>}
              </div>
            )}
          </div>
        </section>

        <section className="mt-10 border border-line bg-parchment p-6" data-testid="decision-block">
          <div className="overline">Decision</div>
          <h3 className="h-serif text-2xl text-ink mt-1">Set KYC status</h3>
          <div className="mt-5 flex flex-wrap gap-3">
            <button onClick={() => decide("cleared")} disabled={busy}
                    className="text-xs font-mono uppercase px-4 py-2.5 border border-success text-success hover:bg-success hover:text-parchment transition-colors"
                    data-testid="kyc-clear">Clear for trading</button>
            <button onClick={() => decide("flagged")} disabled={busy}
                    className="text-xs font-mono uppercase px-4 py-2.5 border border-warn text-warn hover:bg-warn hover:text-parchment transition-colors"
                    data-testid="kyc-flag">Flag — needs more info</button>
            <button onClick={() => decide("blocked")} disabled={busy}
                    className="text-xs font-mono uppercase px-4 py-2.5 border border-danger text-danger hover:bg-danger hover:text-parchment transition-colors"
                    data-testid="kyc-block">Block account</button>
            <button onClick={() => nav("/admin/compliance")}
                    className="ml-auto text-xs text-sub hover:text-ink" data-testid="kyc-cancel">Cancel</button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
